"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.interpolate = exports.prepareBufferForInterpolation = exports.distributePointsInBuffer = exports.distanceFromRepetition = exports.angle2FromRepetition = exports.angleFromRepetition = exports.random = exports.noise = exports.relativeClamp = exports.clamp = exports.lerp = exports.toRadians = exports.toDegrees = exports.now = void 0;
const SimplexNoise = require("simplex-noise");
const repetitions_1 = require("./types/repetitions");
const Vec2_1 = require("./math/Vec2");
const measurement = typeof performance !== 'undefined' ? performance : Date;
/**
 * Get current timestamp in milliseconds
 *
 * @category Utilities
 * @returns {number}
 */
function now() {
    return measurement.now();
}
exports.now = now;
// aOr: (...args: Array<any>): any => {
// 	for (let i = 0; i < args.length; i++) if (Utilities.isDef(args[i])) return args[i]
// },
/**
 * Convert number from radians to degrees
 *
 * @category Utilities
 *
 * @example
 * ```javascript
 * Urpflanze.toDegrees(Math.PI) // 180
 * ```
 *
 * @param {number} radians
 * @returns {number}
 */
function toDegrees(radians) {
    return (radians * 180) / Math.PI;
}
exports.toDegrees = toDegrees;
/**
 * Convert angle from degrees to radians
 * @example
 * ```javascript
 * Urpflanze.toRadians(180) // 3.141592653589793
 * ```
 *
 * @category Utilities
 * @param {number} degrees
 * @returns {number}
 */
function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
}
exports.toRadians = toRadians;
/**
 * Linear interpolation from `a` when `i` as 0 an `b` when `i' as 1
 *
 * @category Utilities
 * @param {number} a
 * @param {number} b
 * @param {number} i
 * @returns {number}
 */
function lerp(a, b, i) {
    return (1 - i) * a + i * b;
}
exports.lerp = lerp;
/**
 * Return number between min and max
 *
 * @category Utilities
 * @example
 * ```javascript
 * Urpflanze.clamp(0, 1, 1.2) // 1
 * Urpflanze.clamp(0, 1, -2) // 0
 * ```
 * @param {number} min
 * @param {number} max
 * @param {number} value
 * @returns {number}
 */
function clamp(min, max, value) {
    return value <= min ? min : value >= max ? max : value;
}
exports.clamp = clamp;
/**
 * Map number between refMin e refMax from min and max
 *
 * @category Utilities
 *
 * @example
 * ```javascript
 * Urpflanze.relativeClamp(0, 1, 0.5, 100, 200) // 150
 * ```
 *
 * @param {number} refMin
 * @param {number} refMax
 * @param {number} value
 * @param {number} toMin
 * @param {number} toMax
 * @returns {number}
 */
function relativeClamp(refMin, refMax, value, toMin, toMax) {
    return clamp(toMin, toMax, ((value - refMin) / (refMax - refMin)) * (toMax - toMin) + toMin);
}
exports.relativeClamp = relativeClamp;
/**
 * @internal
 * @ignore
 */
const noises = {
    random: new SimplexNoise(Math.random),
};
/**
 * <a href="https://github.com/jwagner/simplex-noise.js" target="_blank">SimplexNoise</a>
 * Use 'random' as seed property for random seed.
 * Return value between -1 and 1
 *
 * @category Utilities
 *
 * @param {string} [seed='random']
 * @param {number} [x=0]
 * @param {number} [y=0]
 * @param {number} [z=0]
 * @returns {number} between -1 and 1
 */
function noise(seed = 'random', x = 0, y = 0, z = 0) {
    if (typeof noises[seed] === 'undefined') {
        noises[seed] = new SimplexNoise(seed);
    }
    return noises[seed].noise3D(x, y, z);
}
exports.noise = noise;
/**
 * @internal
 * @ignore
 */
const randoms = {};
/**
 * Random number generator
 * @example
 * ```javascript
 * 	Urpflanze.random('seed') // 0.9367527104914188
 * ```
 *
 * @category Utilities
 * @param {string} seed
 * @param {number} min
 * @param {number} max
 * @param {number} decimals
 * @returns {number}
 */
function random(seed, min = 0, max = 1, decimals) {
    const key = seed + '';
    if (typeof randoms[key] === 'undefined') {
        const seed = xmur3(key);
        randoms[key] = sfc32(seed(), seed(), seed(), seed());
    }
    const value = min + randoms[key]() * (max - min);
    return typeof decimals !== 'undefined' ? Math.round(value * 10 ** decimals) / 10 ** decimals : value;
}
exports.random = random;
/**
 *
 * @internal
 * @param str
 * @returns
 */
function xmur3(str) {
    let i = 0, h = 1779033703 ^ str.length;
    for (; i < str.length; i++)
        (h = Math.imul(h ^ str.charCodeAt(i), 3432918353)), (h = (h << 13) | (h >>> 19));
    return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return (h ^= h >>> 16) >>> 0;
    };
}
/**
 * @internal
 * @param a
 * @param b
 * @param c
 * @param d
 * @returns
 */
function sfc32(a, b, c, d) {
    return function () {
        a >>>= 0;
        b >>>= 0;
        c >>>= 0;
        d >>>= 0;
        let t = (a + b) | 0;
        a = b ^ (b >>> 9);
        b = (c + (c << 3)) | 0;
        c = (c << 21) | (c >>> 11);
        d = (d + 1) | 0;
        t = (t + d) | 0;
        c = (c + t) | 0;
        return (t >>> 0) / 4294967296;
    };
}
/**
 * Return angle (atan) from offset (or center) for matrix repetition.
 * Offset is array between [-1, -1] and [1, 1].
 * The return value is between -Math.PI / 2 and Math.PI / 2
 *
 * @category Utilities
 *
 * @param {IRepetition} repetition
 * @param {[number, number]} offsetFromCenter
 * @returns {number} between -Math.PI / 2 and Math.PI / 2
 */
function angleFromRepetition(repetition, offsetFromCenter = [0, 0]) {
    if (repetition.type === repetitions_1.ERepetitionType.Matrix) {
        const centerMatrix = [(repetition.col.count - 1) / 2, (repetition.row.count - 1) / 2];
        centerMatrix[0] += centerMatrix[0] * offsetFromCenter[0];
        centerMatrix[1] += centerMatrix[1] * offsetFromCenter[1];
        const x = repetition.col.index - 1 - centerMatrix[0];
        const y = repetition.row.index - 1 - centerMatrix[1];
        return x === 0 ? 0 : Math.atan(y / x);
    }
    return (repetition.angle - Math.PI) / 2;
}
exports.angleFromRepetition = angleFromRepetition;
/**
 * Return angle (atan2, 4 quadrants) from offset (or center) for matrix repetition.
 * Offset is array between [-1, -1] and [1, 1].
 * The return value is between -Math.PI an Math.PI
 *
 * @category Utilities
 *
 * @param {IRepetition} repetition
 * @param {[number, number]} offsetFromCenter
 * @returns {number} between -Math.PI an Math.PI
 */
function angle2FromRepetition(repetition, offsetFromCenter = [0, 0]) {
    if (repetition.type === repetitions_1.ERepetitionType.Matrix) {
        const centerMatrix = [(repetition.col.count - 1) / 2, (repetition.row.count - 1) / 2];
        centerMatrix[0] += centerMatrix[0] * offsetFromCenter[0];
        centerMatrix[1] += centerMatrix[1] * offsetFromCenter[1];
        const x = repetition.col.index - 1 - centerMatrix[0];
        const y = repetition.row.index - 1 - centerMatrix[1];
        return x === 0 ? 0 : Math.atan2(y, x);
    }
    return repetition.angle - Math.PI;
}
exports.angle2FromRepetition = angle2FromRepetition;
/**
 * Return distance from offset (or center) for matrix repetition.
 * The return value is between 0 and 1
 *
 * @category Utilities
 *
 * @param {IRepetition} repetition
 * @param {[number, number]} offsetFromCenter offset relative to distance prop
 * @returns {number} between 0 and 1
 */
function distanceFromRepetition(repetition, offsetFromCenter = [0, 0]) {
    if (repetition.type === repetitions_1.ERepetitionType.Matrix) {
        const centerMatrix = [0.5, 0.5];
        centerMatrix[0] += centerMatrix[0] * offsetFromCenter[0];
        centerMatrix[1] += centerMatrix[1] * offsetFromCenter[1];
        const current = [repetition.col.offset, repetition.row.offset];
        return Vec2_1.default.distance(current, centerMatrix);
    }
    return 1;
}
exports.distanceFromRepetition = distanceFromRepetition;
/// Interpolation
/**
 * Evenly distributes a number of points in a buffer
 *
 * @category Utilities.Buffer interpolation
 * @export
 * @param {Float32Array} buffer current buffer
 * @param {number} pointsToAdd points to add
 * @return {*}  {Float32Array}
 */
function distributePointsInBuffer(buffer, pointsToAdd) {
    const bufferLen = buffer.length;
    const pointsLen = bufferLen / 2;
    const finalBufferLength = (pointsLen + pointsToAdd) * 2;
    const edges = pointsLen - 1;
    if (edges > 1) {
        const lastPoint = bufferLen - 2;
        const newPointsOnEdge = Math.floor(pointsToAdd / edges);
        const bufferWithPointsEveryEdge = bufferLen + newPointsOnEdge * lastPoint;
        let remainingPoints = (finalBufferLength - bufferWithPointsEveryEdge) / 2;
        const edgeRemainingIndex = Math.round(edges / remainingPoints);
        const result = new Float32Array(finalBufferLength);
        for (let i = 0, edgeIndex = 0, r = 0; i < lastPoint; i += 2, edgeIndex++, r += 2) {
            const ax = buffer[i];
            const ay = buffer[i + 1];
            const bx = buffer[i + 2];
            const by = buffer[i + 3];
            result[r] = ax;
            result[r + 1] = ay;
            const addReminingPoints = remainingPoints > 0 && (edgeIndex % edgeRemainingIndex === 0 || i === lastPoint - 2);
            const currentPointsOnEdge = newPointsOnEdge + (addReminingPoints ? 1 : 0);
            const newPointOffset = 1 / (currentPointsOnEdge + 1);
            for (let h = 0; h < currentPointsOnEdge; h++, r += 2) {
                const o = newPointOffset * (h + 1);
                result[r + 2] = (1 - o) * ax + o * bx;
                result[r + 3] = (1 - o) * ay + o * by;
            }
            if (addReminingPoints) {
                remainingPoints--;
            }
        }
        result[finalBufferLength - 2] = buffer[bufferLen - 2];
        result[finalBufferLength - 1] = buffer[bufferLen - 1];
        return result;
    }
    const result = new Float32Array(finalBufferLength);
    for (let i = 0; i < finalBufferLength; i += 2) {
        result[i] = buffer[i % bufferLen];
        result[i + 1] = buffer[(i + 1) % bufferLen];
    }
    return result;
}
exports.distributePointsInBuffer = distributePointsInBuffer;
/**
 * Leads two buffers to have the same number of points
 *
 * @category Utilities.Buffer interpolation
 * @param from
 * @param to
 * @returns
 */
function prepareBufferForInterpolation(from, to) {
    const fromBufferLength = from.length;
    const toBufferLength = to.length;
    if (fromBufferLength === toBufferLength) {
        return [from, to];
    }
    // const maxBufferLength = fromBufferLength > toBufferLength ? fromBufferLength : toBufferLength
    const difference = Math.abs(fromBufferLength - toBufferLength);
    // const minBufferLength = maxBufferLength - difference
    /////
    const b = fromBufferLength < toBufferLength ? to : from;
    const t = fromBufferLength < toBufferLength ? from : to;
    const a = distributePointsInBuffer(t, Math.floor(difference / 2));
    // a[maxBufferLength - 2] = t[minBufferLength - 2]
    // a[maxBufferLength - 1] = t[minBufferLength - 1]
    return fromBufferLength > toBufferLength ? [b, a] : [a, b];
}
exports.prepareBufferForInterpolation = prepareBufferForInterpolation;
/**
 * Interpolate two buffer
 *
 * @category Utilities.Buffer interpolation
 * @param from
 * @param to
 * @param offset
 * @returns
 */
function interpolate(from, to, initialOffset = 0.5) {
    const [a, b] = prepareBufferForInterpolation(from, to);
    const maxBufferLength = Math.max(a.length, b.length);
    const offset = typeof initialOffset === 'number' ? [initialOffset] : initialOffset;
    const maxPoints = maxBufferLength / 2;
    if (offset.length !== maxPoints) {
        const tl = offset.length;
        for (let i = 0; i < maxPoints; i++) {
            offset[i] = offset[i % tl];
        }
    }
    ////
    const result = new Float32Array(maxBufferLength);
    for (let i = 0, off = 0; i < maxBufferLength; i += 2, off++) {
        result[i] = (1 - offset[off]) * a[i] + offset[off] * b[i];
        result[i + 1] = (1 - offset[off]) * a[i + 1] + offset[off] * b[i + 1];
    }
    return result;
}
exports.interpolate = interpolate;
//# sourceMappingURL=Utilities.js.map